import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowRight, Layout } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'

interface MoveImageDialogProps {
  open: boolean
  onClose: () => void
  imageId: string | null
  currentSceneId: string
}

const MoveImageDialog: React.FC<MoveImageDialogProps> = ({ 
  open, 
  onClose, 
  imageId, 
  currentSceneId 
}) => {
  const { scenes, moveImageToScene, loading } = useProjectStore()
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null)

  // Filter out the current scene
  const availableScenes = scenes.filter(scene => scene.id !== currentSceneId)

  const handleMove = async () => {
    if (!imageId || !selectedSceneId) return

    try {
      await moveImageToScene(imageId, selectedSceneId)
      onClose()
      setSelectedSceneId(null)
    } catch (error) {
      console.error('Failed to move image:', error)
    }
  }

  const handleClose = () => {
    setSelectedSceneId(null)
    onClose()
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-md border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-blue-300" />
              </div>
              <h2 className="text-xl font-semibold text-white">Move to Scene</h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {availableScenes.length === 0 ? (
              <div className="text-center py-8">
                <Layout className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/70">No other scenes available.</p>
                <p className="text-white/50 text-sm mt-2">Create another scene first to move images.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-white">
                  Select Destination Scene
                </label>
                <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                  {availableScenes.map((scene) => (
                    <button
                      key={scene.id}
                      onClick={() => setSelectedSceneId(scene.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedSceneId === scene.id
                          ? 'bg-blue-500/20 border-blue-500/50 text-white'
                          : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <div className="font-medium">{scene.title}</div>
                      {scene.description && (
                        <div className="text-xs opacity-70 truncate mt-1">{scene.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-6 mt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 glass-button text-white/90 hover:bg-white/10 py-2.5"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleMove}
                className="flex-1 glass-button bg-blue-500/30 hover:bg-blue-500/40 text-white font-semibold py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !selectedSceneId}
              >
                {loading ? 'Moving...' : 'Move Image'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default MoveImageDialog

